import { useCallback, useEffect, useState } from 'react'
import Header from '@/components/layout/Header'
import Panel, { panelGhostButton, panelIconButton } from '@/components/layout/Panel'
import { ChevronLeft, ChevronRight, RefreshCw, Users, CalendarDays, Swords } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'

/**
 * Dashboard → Takvim.
 *
 * Ay ızgarası tek bir cam banttır, kendi kutusu değil: başlık satırı bandın
 * başlığıdır, ay gezinmesi onun eylem yeridir, ızgara da bandın gövdesini
 * tam kanama doldurur.
 *
 * **Tür rengi dolgu değil kenar çizgisidir.** Bir ayda onlarca kayıt olabilir;
 * her biri kendi renginde dolu bir kutu olsaydı ızgara üç renkli bir mozaik
 * olur ve kızıl — panelde yalnızca durum işareti olan renk — en büyük yüzey
 * haline gelirdi. Dolgu nötr, tür solundaki iki piksellik çizgide.
 */

interface CalendarEvent {
  id: string
  title: string
  start_time: number
  active_count: number
  type: 'event' | 'match'
  is_external?: boolean
}

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate()
}

function getFirstDayOfMonth(year: number, month: number) {
  return (new Date(year, month, 1).getDay() + 6) % 7
}

/** Türün rengi — hem kayıt çizgisinde hem açıklama pulunda aynı sabit. */
function typeColor(item: CalendarEvent): string {
  if (item.type !== 'match') return 'var(--primary)'
  return item.is_external ? 'rgb(167 139 250)' : 'rgb(96 165 250)'
}

export default function Calendar() {
  const { T, lang } = useLanguage()
  const locale = lang === 'tr' ? 'tr-TR' : 'en-GB'
  const DAYS = T.calendar.days
  const MONTHS = T.calendar.months
  const today = new Date()
  const [year, setYear] = useState(today.getFullYear())
  const [month, setMonth] = useState(today.getMonth())
  const [items, setItems] = useState<CalendarEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  const formatTime = (ts: number): string =>
    new Date(ts * 1000).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })

  const fetchData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true)
    else setLoading(true)

    const safeFetch = (url: string) =>
      fetch(url, { credentials: 'include' }).then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })

    const [eventsRes, matchesRes] = await Promise.allSettled([
      safeFetch('/api/events'),
      safeFetch('/api/matches'),
    ])

    const merged: CalendarEvent[] = []

    if (eventsRes.status === 'fulfilled') {
      for (const e of eventsRes.value) {
        const ts = e.start_time
        if (ts) {
          merged.push({
            id: e.id,
            title: e.title,
            start_time: ts,
            active_count: e.active_count ?? 0,
            type: 'event',
          })
        }
      }
    }

    if (matchesRes.status === 'fulfilled') {
      for (const m of matchesRes.value) {
        const ev = m.event
        if (ev?.start_time) {
          merged.push({
            id: m.event_id,
            title: ev.title,
            start_time: ev.start_time,
            active_count: ev.active_count ?? 0,
            type: 'match',
            is_external: ev.is_external,
          })
        }
      }
    }

    setItems(merged)
    setLoading(false)
    setRefreshing(false)
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1) }
    else setMonth(m => m - 1)
  }
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1) }
    else setMonth(m => m + 1)
  }

  const daysInMonth = getDaysInMonth(year, month)
  const firstDay = getFirstDayOfMonth(year, month)

  // Build lookup using local timezone: { "YYYY-M-D": [item, ...] }
  const itemsByDay: Record<string, CalendarEvent[]> = {}
  for (const item of items) {
    const d = new Date(item.start_time * 1000) // auto local timezone
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
    if (!itemsByDay[key]) itemsByDay[key] = []
    itemsByDay[key].push(item)
  }
  // Sort each day's items by start_time
  for (const key of Object.keys(itemsByDay)) {
    itemsByDay[key].sort((a, b) => a.start_time - b.start_time)
  }

  const cells: (number | null)[] = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ]
  while (cells.length % 7 !== 0) cells.push(null)

  const isCurrentMonth = month === today.getMonth() && year === today.getFullYear()
  const monthHasItems = cells.some(
    day => day !== null && (itemsByDay[`${year}-${month}-${day}`]?.length ?? 0) > 0,
  )

  const legend: { color: string; label: string }[] = [
    { color: 'var(--primary)',     label: T.calendar.eventLegend },
    { color: 'rgb(96 165 250)',    label: T.calendar.matchLegend },
    { color: 'rgb(167 139 250)',   label: T.calendar.externalMatch },
  ]

  const dayHeaders = (
    <div className="grid grid-cols-7 border-b border-white/10">
      {DAYS.map(d => (
        <div
          key={d}
          className="py-2 text-center font-display text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground/70"
        >
          {d}
        </div>
      ))}
    </div>
  )

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <Header title={T.calendar.title} />

      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <Panel
          title={`${MONTHS[month]} ${year}`}
          bodyClassName=""
          className="overflow-hidden"
          action={
            <>
              <button
                className={panelIconButton}
                onClick={() => fetchData(true)}
                disabled={refreshing}
                title={T.calendar.refresh}
                aria-label={T.calendar.refresh}
              >
                <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                className={panelIconButton}
                onClick={prevMonth}
                aria-label={MONTHS[(month + 11) % 12]}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                className={panelGhostButton}
                onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth()) }}
              >
                {T.calendar.today}
              </button>
              <button
                className={panelIconButton}
                onClick={nextMonth}
                aria-label={MONTHS[(month + 1) % 12]}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </>
          }
        >
          {/* Açıklama şeridi — pullar kayıtların kenar çizgisinin aynısı */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-b border-white/10 px-5 py-2.5 text-[11px] text-muted-foreground">
            {legend.map(l => (
              <span key={l.label} className="flex items-center gap-1.5">
                <span className="h-2.5 w-0.5 shrink-0" style={{ background: l.color }} />
                {l.label}
              </span>
            ))}
            {!loading && !monthHasItems && (
              <span className="ml-auto text-muted-foreground/50">{T.calendar.noEvents}</span>
            )}
          </div>

          {loading ? (
            <>
              {dayHeaders}
              <div className="grid grid-cols-7">
                {Array.from({ length: 35 }).map((_, i) => (
                  <div key={i} className="min-h-[92px] border-b border-r border-white/8 p-1.5">
                    <div className="h-5 w-5 animate-pulse rounded-sm bg-white/8" />
                    {i % 5 === 1 && <div className="mt-2 h-6 w-4/5 animate-pulse rounded-sm bg-white/5" />}
                    {i % 7 === 3 && (
                      <>
                        <div className="mt-2 h-6 w-3/4 animate-pulse rounded-sm bg-white/5" />
                        <div className="mt-1 h-6 w-2/3 animate-pulse rounded-sm bg-white/5" />
                      </>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              {dayHeaders}

              {/* Gün hücreleri */}
              <div className="grid grid-cols-7">
                {cells.map((day, idx) => {
                  const key = day ? `${year}-${month}-${day}` : null
                  const dayItems = key ? (itemsByDay[key] ?? []) : []
                  const isToday = day === today.getDate() && isCurrentMonth
                  const isPast = day !== null && (
                    year < today.getFullYear() ||
                    (year === today.getFullYear() && month < today.getMonth()) ||
                    (isCurrentMonth && day < today.getDate())
                  )

                  return (
                    <div
                      key={idx}
                      className={`min-h-[92px] border-b border-r border-white/8 p-1.5 ${
                        !day ? 'bg-black/25' : isPast ? 'bg-black/15' : ''
                      }`}
                    >
                      {day && (
                        <>
                          {/* Bugün dolu bir pul — panelde kızılın yüzey olduğu
                              tek yer bir durum işaretidir, bu o. */}
                          <span className={`inline-flex h-5 w-5 items-center justify-center rounded-sm font-display text-[11px] font-bold tabular-nums ${
                            isToday
                              ? 'bg-primary text-primary-foreground'
                              : isPast ? 'text-muted-foreground/40' : 'text-muted-foreground'
                          }`}>
                            {day}
                          </span>
                          <div className="mt-1 space-y-1">
                            {dayItems.map(item => {
                              const Icon = item.type === 'match' ? Swords : CalendarDays
                              const color = typeColor(item)

                              return (
                                <div
                                  key={item.id}
                                  className={`group relative rounded-sm border border-white/10 bg-white/[0.035] py-0.5 pl-1.5 pr-1 text-[10px] leading-tight transition-colors hover:border-white/25 ${
                                    isPast ? 'opacity-45' : ''
                                  }`}
                                  style={{ boxShadow: `inset 2px 0 0 0 ${color}` }}
                                >
                                  <div className="flex items-center gap-1">
                                    <Icon className="h-2.5 w-2.5 shrink-0" style={{ color }} />
                                    {/* Başlık veridir: büyütülmez. */}
                                    <span className="flex-1 truncate font-medium text-foreground/85">
                                      {item.title}
                                    </span>
                                  </div>
                                  <div className="mt-0.5 flex items-center gap-1.5 text-[9px] text-muted-foreground/70">
                                    <span className="tabular-nums">{formatTime(item.start_time)}</span>
                                    <span className="flex items-center gap-0.5 tabular-nums">
                                      <Users className="h-2 w-2" />
                                      {item.active_count}
                                    </span>
                                  </div>

                                  {/* Tooltip */}
                                  <div className="absolute bottom-full left-1/2 z-30 mb-1 hidden -translate-x-1/2 group-hover:block">
                                    <div className="whitespace-nowrap rounded-sm border border-white/15 bg-[#0b0b0d] px-2.5 py-1.5 text-[11px] shadow-xl">
                                      <p className="font-semibold">{item.title}</p>
                                      <p className="mt-0.5 text-muted-foreground">
                                        {new Date(item.start_time * 1000).toLocaleString(locale, {
                                          weekday: 'short', day: 'numeric', month: 'short',
                                          hour: '2-digit', minute: '2-digit',
                                        })}
                                      </p>
                                      <p className="mt-0.5 flex items-center gap-1 text-muted-foreground">
                                        <Users className="h-2.5 w-2.5" />
                                        {item.active_count} {T.calendar.signedUp}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </Panel>
      </div>
    </div>
  )
}
